import { useCallback } from "react";
import formatUuidT from "../../../flatbuffers/display/uuid";
import {
  useLazyGetLeadingZeroesByIdQuery,
  usePostLeadingZeroesMutation,
} from "../../../redux/LeadingZeroes/leadingZeroesAPI";

/** The seven flags of a leading_zeroes row, in the order the editor shows them. */
export interface LeadingZeroesFlags {
  fromDeviceSerial: boolean;
  refreshAll: boolean;
  setOneBit: boolean;
  reinitializeAllDevices: boolean;
  setOneByte: boolean;
  rvu: boolean;
  rvuHeartbeat: boolean;
}

export const EMPTY_LEADING_ZEROES_FLAGS: LeadingZeroesFlags = {
  fromDeviceSerial: false,
  refreshAll: false,
  setOneBit: false,
  reinitializeAllDevices: false,
  setOneByte: false,
  rvu: false,
  rvuHeartbeat: false,
};

const SNAKE_CASE_KEYS: Record<keyof LeadingZeroesFlags, string> = {
  fromDeviceSerial: "from_device_serial",
  refreshAll: "refresh_all",
  setOneBit: "set_one_bit",
  reinitializeAllDevices: "reinitialize_all_devices",
  setOneByte: "set_one_byte",
  rvu: "rvu",
  rvuHeartbeat: "rvu_heartbeat",
};

const FLAG_KEYS = Object.keys(SNAKE_CASE_KEYS) as (keyof LeadingZeroesFlags)[];

export const leadingZeroesFlagsEqual = (a: LeadingZeroesFlags, b: LeadingZeroesFlags): boolean =>
  FLAG_KEYS.every((key) => a[key] === b[key]);

/** A row id as a string: FlatBuffer replies carry a UuidT, JSON replies a string. */
const idToString = (id: unknown): string => {
  if (typeof id === "string") return id;
  if (typeof id === "object" && id !== null)
    return formatUuidT(id as Parameters<typeof formatUuidT>[0]);
  return "";
};

/** Reads the flags off a row, whichever spelling the reply used (camelCase or snake_case). */
export const flagsFromRow = (row: Record<string, unknown>): LeadingZeroesFlags => {
  const flags = { ...EMPTY_LEADING_ZEROES_FLAGS };
  FLAG_KEYS.forEach((key) => {
    flags[key] = Boolean(row[key] ?? row[SNAKE_CASE_KEYS[key]]);
  });
  return flags;
};

/** The JSON body POST /leading-zeroes/ takes for a new row. */
export const flagsToBody = (flags: LeadingZeroesFlags, name: string): Record<string, unknown> => {
  const body: Record<string, unknown> = { name };
  FLAG_KEYS.forEach((key) => {
    body[SNAKE_CASE_KEYS[key]] = flags[key];
  });
  return body;
};

/** The id of the row a create reply describes (`{data: row}` or the row itself). */
export const createdRowId = (reply: unknown): string => {
  if (typeof reply !== "object" || reply === null) return "";
  const envelope = reply as { data?: unknown; id?: unknown };
  const row = typeof envelope.data === "object" && envelope.data !== null ? envelope.data : reply;
  return idToString((row as { id?: unknown }).id);
};

/** The flags a create reply echoes back, or null when the reply carries none. */
export const createdRowFlags = (reply: unknown): LeadingZeroesFlags | null => {
  if (typeof reply !== "object" || reply === null) return null;
  const envelope = reply as { data?: unknown };
  const row = typeof envelope.data === "object" && envelope.data !== null ? envelope.data : reply;
  const record = row as Record<string, unknown>;
  const echoesAFlag = FLAG_KEYS.some(
    (key) => record[key] !== undefined || record[SNAKE_CASE_KEYS[key]] !== undefined,
  );
  return echoesAFlag ? flagsFromRow(record) : null;
};

/**
 * The leading_zeroes id a save should send. Flags unchanged since load keep the
 * current link; changed flags create a NEW row through `create`. The linked row
 * is never edited: it may be shared by other adapters and channels. When the
 * linked row's flags are unknown (`loaded` is null) the link is kept as it is.
 */
export const resolveLeadingZeroesLink = async (
  currentId: string,
  loaded: LeadingZeroesFlags | null,
  edited: LeadingZeroesFlags,
  create: (flags: LeadingZeroesFlags) => Promise<string>,
): Promise<string> => {
  if (!loaded || leadingZeroesFlagsEqual(edited, loaded)) return currentId;
  return create(edited);
};

/**
 * Reads and creates leading_zeroes rows for the adapter editor. It never
 * updates or deletes one: a row can be shared by many adapters and channels,
 * so a change to the flags is always a NEW row for this adapter.
 */
export const useLeadingZeroesRows = () => {
  const [triggerGet] = useLazyGetLeadingZeroesByIdQuery();
  const [triggerPost] = usePostLeadingZeroesMutation();

  const loadFlags = useCallback(
    async (id: string, token: string): Promise<LeadingZeroesFlags | null> => {
      const reply = await triggerGet({ id, token }).unwrap();
      const row = reply?.data ?? reply;
      return row && typeof row === "object" ? flagsFromRow(row) : null;
    },
    [triggerGet],
  );

  const createRow = useCallback(
    async (flags: LeadingZeroesFlags, name: string, token: string): Promise<string> => {
      const reply = await triggerPost({ body: flagsToBody(flags, name), token }).unwrap();
      const id = createdRowId(reply);
      if (!id) throw new Error("Leading zeroes were created but the reply carried no id");
      // A server that does not know a flag stores it as false and still answers 201.
      // Linking to that row would show a ticked box the CDO never receives.
      const stored = createdRowFlags(reply);
      if (stored && !leadingZeroesFlagsEqual(stored, flags)) {
        throw new Error(
          "The server stored different leading zeroes than were sent (an unused record was created); it may need updating.",
        );
      }
      return id;
    },
    [triggerPost],
  );

  return { loadFlags, createRow };
};
